import { useMemo, useState } from 'react';
import { api, ApiError, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useRefData } from '../../lib/refdata';
import type { ArrearsRow, BurnRateResponse, BvaResponse, BvaRow, OagExport, PublishedReport } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, PageHeader, Spinner, StatCard, StatusBadge } from '../../ui/components';

export function BudgetVsActual() {
  const { persona } = useAuth();
  const { fy, votes } = useRefData();
  const [drillVoteId, setDrillVoteId] = useState<string | null>(null);
  const [reports, setReports] = useState<PublishedReport[]>([]);
  const [oagExport, setOagExport] = useState<OagExport | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const isAnalyst = persona?.roles.includes('MOF_ANALYST') ?? false;
  const canSign = (persona?.roles.includes('MDA_PS') || persona?.roles.includes('PS_FINANCE')) ?? false;
  const isOagAuditor = persona?.roles.includes('OAG_AUDITOR') ?? false;

  const bvaQ = useApiQuery<BvaResponse>(fy ? `/api/v1/fiscal/bva?fiscalYearId=${fy.id}&level=vote` : null);
  const arrearsQ = useApiQuery<{ items: ArrearsRow[] }>(fy ? `/api/v1/fiscal/arrears?fiscalYearId=${fy.id}` : null);

  const voteName = (id: string) => {
    const v = votes.find((x) => x.id === id);
    return v ? `${v.code} — ${v.name}` : id;
  };

  const totals = useMemo(() => {
    const rows = bvaQ.data?.items ?? [];
    const sum = (fn: (r: BvaRow) => number) => rows.reduce((s, r) => s + fn(r), 0);
    const appropriated = sum((r) => r.APPROPRIATED);
    const paid = sum((r) => r.PAID);
    return {
      appropriated,
      allotted: sum((r) => r.ALLOTTED),
      paid,
      executionPct: appropriated > 0 ? Math.round((paid / appropriated) * 1000) / 10 : null,
    };
  }, [bvaQ.data]);

  const totalArrears = (arrearsQ.data?.items ?? []).reduce((s, r) => s + r.arrears, 0);

  const patchReport = (id: string, patch: Partial<PublishedReport>) =>
    setReports((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const runOagExport = async () => {
    if (!fy) return;
    setActionError(null);
    try {
      const result = await api.get<OagExport>(`/api/v1/fiscal/oag/export?fiscalYearId=${fy.id}`);
      setOagExport(result);
    } catch (e) {
      setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Export failed');
    }
  };

  if (!fy) return <ErrorNote message="No OPEN fiscal year." />;

  return (
    <div>
      <PageHeader
        title="Budget vs Actual"
        subtitle={`Fiscal Year ${fy.year} — live burn rate against the enacted budget, the Cabinet fiscal dashboard`}
      />
      {actionError ? (
        <div className="mb-4">
          <ErrorNote message={actionError} />
        </div>
      ) : null}

      <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Appropriated (all votes)" value={scr(totals.appropriated)} />
        <StatCard label="Allotted" value={scr(totals.allotted)} />
        <StatCard label="Paid to date" value={scr(totals.paid)} tone="good" />
        <StatCard
          label="National execution rate"
          value={totals.executionPct !== null ? `${totals.executionPct}%` : '—'}
          tone={totals.executionPct !== null && totals.executionPct > 90 ? 'warn' : 'default'}
          hint={`Arrears watch: ${scr(totalArrears)}`}
        />
      </div>

      <div className="mb-6">
        <h2 className="mb-2 text-sm font-bold text-slate-700">Budget vs actual by vote</h2>
        <div className="card overflow-x-auto">
          {bvaQ.loading ? (
            <Spinner />
          ) : bvaQ.error ? (
            <ErrorNote message={bvaQ.error} />
          ) : (bvaQ.data?.items.length ?? 0) === 0 ? (
            <EmptyState title="No postings yet" hint="F02–F06/F09 modules post their stage movements here as they process." />
          ) : (
            <table className="w-full">
              <thead>
                <tr>
                  <th className="table-head">Vote</th>
                  <th className="table-head text-right">Appropriated</th>
                  <th className="table-head text-right">Allotted</th>
                  <th className="table-head text-right">Committed</th>
                  <th className="table-head text-right">Accrued</th>
                  <th className="table-head text-right">Paid</th>
                  <th className="table-head w-44">Execution</th>
                </tr>
              </thead>
              <tbody>
                {bvaQ
                  .data!.items.slice()
                  .sort((a, b) => b.APPROPRIATED - a.APPROPRIATED)
                  .map((r) => {
                    const pct = r.executionPct ?? 0;
                    return (
                      <tr
                        key={r.vote}
                        className="cursor-pointer hover:bg-slate-50"
                        onClick={() => setDrillVoteId(drillVoteId === r.vote ? null : (r.vote ?? null))}
                      >
                        <td className="table-cell font-medium">{voteName(r.vote!)}</td>
                        <td className="table-cell text-right tabular-nums">{scr(r.APPROPRIATED)}</td>
                        <td className="table-cell text-right tabular-nums">{scr(r.ALLOTTED)}</td>
                        <td className="table-cell text-right tabular-nums">{scr(r.COMMITTED)}</td>
                        <td className="table-cell text-right tabular-nums">{scr(r.ACCRUED)}</td>
                        <td className="table-cell text-right tabular-nums">{scr(r.PAID)}</td>
                        <td className="table-cell">
                          <div className="h-2 w-full rounded-full bg-slate-100">
                            <div
                              className={`h-2 rounded-full ${pct > 95 ? 'bg-rose-500' : pct > 80 ? 'bg-amber-500' : 'bg-brand-500'}`}
                              style={{ width: `${Math.min(100, Math.max(2, pct))}%` }}
                            />
                          </div>
                          <div className="mt-0.5 text-[11px] text-slate-400">{r.executionPct ?? '—'}% of appropriated paid</div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {drillVoteId ? <BurnRateDrilldown voteId={drillVoteId} fyId={fy.id} voteName={voteName} /> : null}

      <div className="mb-6">
        <h2 className="mb-2 text-sm font-bold text-slate-700">Arrears watch — accrued but unpaid</h2>
        <div className="card overflow-x-auto">
          {arrearsQ.loading ? (
            <Spinner />
          ) : arrearsQ.error ? (
            <ErrorNote message={arrearsQ.error} />
          ) : (arrearsQ.data?.items.length ?? 0) === 0 ? (
            <EmptyState title="No arrears — all accrued expenditure has been paid" />
          ) : (
            <table className="w-full">
              <thead>
                <tr>
                  <th className="table-head">Vote</th>
                  <th className="table-head text-right">Accrued</th>
                  <th className="table-head text-right">Paid</th>
                  <th className="table-head text-right">Arrears</th>
                </tr>
              </thead>
              <tbody>
                {arrearsQ.data!.items.map((r) => (
                  <tr key={r.voteId}>
                    <td className="table-cell text-xs">{voteName(r.voteId)}</td>
                    <td className="table-cell text-right tabular-nums">{scr(r.accrued)}</td>
                    <td className="table-cell text-right tabular-nums">{scr(r.paid)}</td>
                    <td className="table-cell text-right font-semibold tabular-nums text-rose-600">{scr(r.arrears)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {isAnalyst ? <PeriodCloseCard /> : null}

      {isAnalyst || canSign ? (
        <div className="mb-6">
          <h2 className="mb-2 text-sm font-bold text-slate-700">Statutory reports (this session)</h2>
          <p className="mb-2 text-xs text-slate-400">There is no report-listing endpoint yet — reports generated in this tab are tracked here.</p>
          <div className="card overflow-x-auto">
            <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
              <span className="text-xs text-slate-500">Generate the monthly outturn, quarterly, annual FS or GFS report for a period.</span>
              {isAnalyst ? (
                <GenerateReportInline
                  fyId={fy.id}
                  onDone={(report) => setReports((prev) => [report, ...prev])}
                />
              ) : null}
            </div>
            {reports.length === 0 ? (
              <EmptyState title="No reports generated yet" />
            ) : (
              reports.map((r) => (
                <div key={r.id} className="flex items-center justify-between px-4 py-3">
                  <div>
                    <div className="text-sm font-semibold">
                      {r.type.replaceAll('_', ' ')} — {r.period}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={r.status} />
                    {canSign && r.status === 'DRAFT' ? (
                      <button
                        className="btn-success !px-2.5 !py-1 text-xs"
                        onClick={async () => {
                          await api.post(`/api/v1/fiscal/reports/${r.id}/sign`);
                          patchReport(r.id, { status: 'PS_SIGNED' });
                        }}
                      >
                        Sign
                      </button>
                    ) : null}
                    {isAnalyst && r.status === 'PS_SIGNED' ? (
                      <button
                        className="btn-primary !px-2.5 !py-1 text-xs"
                        onClick={async () => {
                          await api.post(`/api/v1/fiscal/reports/${r.id}/submit-oag`);
                          patchReport(r.id, { status: 'SUBMITTED_OAG' });
                        }}
                      >
                        Submit to OAG
                      </button>
                    ) : null}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      ) : null}

      {isOagAuditor ? (
        <div className="mb-6">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-sm font-bold text-slate-700">OAG immutable export</h2>
            <button className="btn-secondary !py-1 text-xs" onClick={runOagExport}>
              Export FY{fy.year} snapshot
            </button>
          </div>
          {oagExport ? (
            <div className="card p-4 text-sm">
              <p className="mb-1">
                {oagExport.snapshot.postingLines} posting lines · exported {oagExport.snapshot.exportedAt}
              </p>
              <p className="font-mono text-xs text-slate-500">sha256 {oagExport.proof.sha256}</p>
            </div>
          ) : (
            <p className="text-xs text-slate-400">Produces a hashed snapshot anchored to the audit chain for the Auditor-General.</p>
          )}
        </div>
      ) : null}
    </div>
  );
}

function BurnRateDrilldown({ voteId, fyId, voteName }: { voteId: string; fyId: string; voteName: (id: string) => string }) {
  const q = useApiQuery<BurnRateResponse>(`/api/v1/fiscal/burn-rate/${voteId}?fiscalYearId=${fyId}`);
  return (
    <div className="mb-6">
      <h2 className="mb-2 text-sm font-bold text-slate-700">Burn rate — {voteName(voteId)}</h2>
      <div className="card overflow-x-auto">
        {q.loading ? (
          <Spinner />
        ) : (q.data?.series.length ?? 0) === 0 ? (
          <EmptyState title="No monthly postings yet for this vote" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Month</th>
                <th className="table-head text-right">Spent</th>
                <th className="table-head text-right">Cumulative</th>
                <th className="table-head w-48">Burn</th>
              </tr>
            </thead>
            <tbody>
              {q.data!.series.map((p) => (
                <tr key={p.period}>
                  <td className="table-cell">{p.period}</td>
                  <td className="table-cell text-right tabular-nums">{scr(p.spent)}</td>
                  <td className="table-cell text-right tabular-nums">{scr(p.cumulativeSpent)}</td>
                  <td className="table-cell">
                    <div className="h-2 w-full rounded-full bg-slate-100">
                      <div className="h-2 rounded-full bg-seagreen-500" style={{ width: `${Math.min(100, Math.max(2, p.burnPct ?? 0))}%` }} />
                    </div>
                    <div className="mt-0.5 text-[11px] text-slate-400">{p.burnPct ?? '—'}% of appropriated</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function PeriodCloseCard() {
  const [period, setPeriod] = useState(() => new Date().toISOString().slice(0, 7));
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const close = async (kind: 'soft-close' | 'hard-close') => {
    setBusy(true);
    setError(null);
    try {
      const result = await api.post<{ status: string }>(`/api/v1/fiscal/periods/${period}/${kind}`);
      setStatus(result.status);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Close failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mb-6">
      <h2 className="mb-2 text-sm font-bold text-slate-700">Period close</h2>
      <div className="card flex flex-wrap items-center gap-3 p-4">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Period" className="w-40">
          <input className="field-input" type="month" value={period} onChange={(e) => setPeriod(e.target.value)} />
        </Field>
        {status ? <StatusBadge status={status} /> : null}
        <button className="btn-secondary" onClick={() => close('soft-close')} disabled={busy}>
          Soft close
        </button>
        <button className="btn-danger" onClick={() => close('hard-close')} disabled={busy}>
          Hard close
        </button>
      </div>
    </div>
  );
}

function GenerateReportInline({ fyId, onDone }: { fyId: string; onDone: (report: PublishedReport) => void }) {
  const [type, setType] = useState<PublishedReport['type']>('MONTHLY_OUTTURN');
  const [period, setPeriod] = useState(() => new Date().toISOString().slice(0, 7));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generate = async () => {
    setBusy(true);
    setError(null);
    try {
      const report = await api.post<PublishedReport>(`/api/v1/fiscal/reports/${type}/generate`, { period, fiscalYearId: fyId });
      onDone(report);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Generate failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      {error ? <span className="text-xs text-rose-600">{error}</span> : null}
      <select className="field-input !py-1 text-xs" value={type} onChange={(e) => setType(e.target.value as PublishedReport['type'])}>
        <option value="MONTHLY_OUTTURN">Monthly outturn</option>
        <option value="QUARTERLY">Quarterly</option>
        <option value="ANNUAL_FS">Annual financial statements</option>
        <option value="GFS">GFS</option>
      </select>
      <input className="field-input !py-1 w-32 text-xs" type="month" value={period} onChange={(e) => setPeriod(e.target.value)} />
      <button className="btn-primary !py-1 text-xs" onClick={generate} disabled={busy}>
        {busy ? 'Generating…' : 'Generate'}
      </button>
    </div>
  );
}
